import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { useSnackbar } from "notistack";

import {
  AppsOutlined,
  AssignmentOutlined,
  CalendarTodayOutlined,
  CheckCircleOutlined,
  CloseOutlined,
  GroupsOutlined,
  HourglassEmptyOutlined,
  LaunchOutlined,
  PaymentsOutlined,
  RefreshOutlined,
  SearchOutlined,
  TableRowsOutlined,
  TrendingUpOutlined,
} from "@mui/icons-material";

import "./admit-applicants.css";

const API = process.env.REACT_APP_SERVER_SCRIPT_HOST;

/* =========================================================
   HELPERS
========================================================= */

const getName = (item) =>
  [
    item?.first_Name,
    item?.sur_Name,
    item?.other_Name,
  ]
    .filter(Boolean)
    .join(" ") || "Unnamed Candidate";

const getInitials = (item) => {
  const name = getName(item);

  return name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("")
    .toUpperCase();
};

const getProgramme = (item) =>
  item?.course_of_study ||
  item?.course_of_study ||
  item?.academicInfo?.programme ||
  "Programme not specified";

const getAdmissionStatus = (item) =>
  item?.admissions?.status || "pending";

const getReferenceNumber = (item) =>
  item?.account_status?.reference_number || "Not assigned";

const getDate = (item) =>
  item?.registered_date?.date ||
  item?.registered_date ||
  item?.createdAt ||
  "—";

const normalizeStatus = (status) =>
  String(status || "pending")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "-");

/* =========================================================
   STATUS BADGE
========================================================= */

function AdmissionStatus({ status }) {
  const normalized = normalizeStatus(status);

  let label = status || "Pending";

  if (normalized === "admitted") {
    label = "Admitted";
  } else if (normalized === "pending") {
    label = "Pending";
  } else if (normalized === "rejected") {
    label = "Rejected";
  } else if (normalized === "approved") {
    label = "Approved";
  }

  return (
    <span
      className={`admission-status admission-status--${normalized}`}
    >
      <span className="admission-status__dot" />
      {label}
    </span>
  );
}

/* =========================================================
   AVATAR
========================================================= */

function CandidateAvatar({ candidate, large = false }) {
  return (
    <div
      className={`candidate-avatar ${
        large ? "candidate-avatar--large" : ""
      }`}
    >
      {candidate?.passport?.url ? (
        <img
          src={candidate.passport.url}
          alt={getName(candidate)}
        />
      ) : (
        <span>{getInitials(candidate)}</span>
      )}
    </div>
  );
}

/* =========================================================
   KPI CARD
========================================================= */

function StatCard({
  icon,
  value,
  label,
  description,
  variant,
}) {
  return (
    <article
      className={`admission-stat admission-stat--${variant}`}
    >
      <div className="admission-stat__header">
        <div className="admission-stat__icon">
          {icon}
        </div>

        <span className="admission-stat__tag">
          LIVE
        </span>
      </div>

      <div className="admission-stat__value">
        {value ?? 0}
      </div>

      <div className="admission-stat__label">
        {label}
      </div>

      <p>{description}</p>
    </article>
  );
}

/* =========================================================
   TABLE
========================================================= */

function TableView({ applicants }) {
  return (
    <section className="admission-table-card">

      <div className="admission-table-card__header">
        <div>
          <span className="section-eyebrow">
            ADMISSION PIPELINE
          </span>

          <h2>Recommended candidates</h2>

          <p>
            Review candidates recommended for admission
            and inspect their application records.
          </p>
        </div>

        <div className="table-result-count">
          <strong>{applicants.length}</strong>
          <span>candidates</span>
        </div>
      </div>

      <div className="admission-table-wrapper">
        <table className="admission-table">
          <thead>
            <tr>
              <th className="sn-column">#</th>
              <th>Candidate</th>
              <th>Application</th>
              <th>Gender</th>
              <th>Programme</th>
              <th>Reference</th>

              {/* ACTION IMMEDIATELY AFTER PROGRAMME */}
              <th>Action</th>

              <th>Admission</th>
              <th>Date</th>
            </tr>
          </thead>

          <tbody>
            {applicants.map((candidate, index) => (
              <tr
                key={
                  candidate?._id ||
                  candidate?.application_number ||
                  index
                }
              >
                <td className="sn-column">
                  {String(index + 1).padStart(2, "0")}
                </td>

                <td>
                  <div className="candidate-cell">
                    <CandidateAvatar candidate={candidate} />

                    <div>
                      <strong>
                        {getName(candidate)}
                      </strong>

                      <span>
                        {candidate?.gender ||
                          "Gender not specified"}
                      </span>
                    </div>
                  </div>
                </td>

                <td>
                  <span className="application-id">
                    {candidate?.application_number ||
                      "N/A"}
                  </span>
                </td>

                <td>
                  <span className="gender-value">
                    {candidate?.gender || "—"}
                  </span>
                </td>

                <td>
                  <div className="programme-cell">
                    <strong>
                      {getProgramme(candidate)}
                    </strong>

                    <span>
                      Recommended programme
                    </span>
                  </div>
                </td>

                <td>
                  <span className="reference-number">
                    {getReferenceNumber(candidate)}
                  </span>
                </td>

                {/* ACTION */}
                <td>
                  <Link
                    to={`/admin_profile/admit_detail/${candidate?._id}`}
                    target="_blank"
                    rel="noreferrer"
                    className="candidate-view-button"
                  >
                    <span>Details</span>
                    <LaunchOutlined />
                  </Link>
                </td>

                <td>
                  <AdmissionStatus
                    status={getAdmissionStatus(candidate)}
                  />
                </td>

                <td>
                  <div className="date-cell">
                    <CalendarTodayOutlined />
                    <span>{getDate(candidate)}</span>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

/* =========================================================
   GRID VIEW
========================================================= */

function CardView({ applicants }) {
  return (
    <section className="admission-grid">
      {applicants.map((candidate, index) => (
        <article
          className="admission-candidate-card"
          key={
            candidate?._id ||
            candidate?.application_number ||
            index
          }
        >
          <div className="candidate-card__header">

            <div className="candidate-card__identity">
              <CandidateAvatar
                candidate={candidate}
                large
              />

              <div>
                <h3>{getName(candidate)}</h3>

                <span>
                  {candidate?.application_number ||
                    "No application number"}
                </span>
              </div>
            </div>

            <AdmissionStatus
              status={getAdmissionStatus(candidate)}
            />
          </div>

          <div className="candidate-card__programme">
            <span>RECOMMENDED PROGRAMME</span>

            <strong>
              {getProgramme(candidate)}
            </strong>
          </div>

          <div className="candidate-card__details">

            <div>
              <span>Gender</span>
              <strong>
                {candidate?.gender || "—"}
              </strong>
            </div>

            <div>
              <span>Reference</span>
              <strong>
                {getReferenceNumber(candidate)}
              </strong>
            </div>

            <div>
              <span>Submitted</span>
              <strong>{getDate(candidate)}</strong>
            </div>

          </div>

          <div className="candidate-card__footer">

            <Link
              to={`/admin_profile/admit_detail/${candidate?._id}`}
              target="_blank"
              rel="noreferrer"
              className="candidate-card__button"
            >
              <AssignmentOutlined />
              View candidate
              <LaunchOutlined />
            </Link>

          </div>
        </article>
      ))}
    </section>
  );
}

/* =========================================================
   EMPTY STATE
========================================================= */

function EmptyState({ searching, clearSearch }) {
  return (
    <section className="admission-empty">

      <div className="admission-empty__icon">
        <GroupsOutlined />
      </div>

      <h3>
        {searching
          ? "No candidates found"
          : "No recommended candidates"}
      </h3>

      <p>
        {searching
          ? "Try another application number or clear your search."
          : "Candidates recommended for admission will appear here."}
      </p>

      {searching && (
        <button
          type="button"
          onClick={clearSearch}
        >
          <CloseOutlined />
          Clear search
        </button>
      )}

    </section>
  );
}

/* =========================================================
   MAIN COMPONENT
========================================================= */

export default function AdmitApplicants() {
  const { enqueueSnackbar } = useSnackbar();

  const [applicants, setApplicants] = useState([]);
  const [counts, setCounts] = useState({
    nursings: 0,
    midwiferys: 0,
  });

  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [view, setView] = useState("grid");

  /* =======================================================
     GET ALL RECOMMENDED APPLICANTS
  ======================================================= */

  const getApplicants = async () => {
    try {
      setLoading(true);

      const response = await axios.get(
        `${API}/admitting_applicant`
      );

      const result = response?.data || {};

      setApplicants(result.response || []);

      setCounts({
        nursings: result.nursings || 0,
        midwiferys: result.midwiferys || 0,
      });
    } catch (error) {
      console.error(error);

      enqueueSnackbar(
        "Unable to load recommended applicants.",
        {
          variant: "error",
          autoHideDuration: 3500,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  /* =======================================================
     SEARCH APPLICATION NUMBER
  ======================================================= */

  const findApplicationNo = async () => {
    if (!searchTerm.trim()) {
      getApplicants();
      return;
    }

    try {
      setLoading(true);

      const response = await axios.post(
        `${API}/admitting_applicant`,
        {
          application_number:
            searchTerm.trim(),
        }
      );

      const result = response?.data || {};

      setApplicants(result.response || []);

      setCounts({
        nursings: result.nursings || 0,
        midwiferys: result.midwiferys || 0,
      });
    } catch (error) {
      console.error(error);

      enqueueSnackbar(
        "Unable to search this application.",
        {
          variant: "error",
          autoHideDuration: 3500,
        }
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    getApplicants();
  }, []);

  /* =======================================================
     LOCAL SEARCH
  ======================================================= */

  const visibleApplicants = useMemo(() => {
    const query = searchTerm
      .trim()
      .toLowerCase();

    if (!query) return applicants;

    return applicants.filter((candidate) => {
      const searchable = [
        getName(candidate),
        candidate?.application_number,
        candidate?.course_of_study,
        candidate?.gender,
        candidate?.account_status?.reference_number,
        candidate?.admissions?.status,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return searchable.includes(query);
    });
  }, [applicants, searchTerm]);

  const clearSearch = () => {
    setSearchTerm("");
    getApplicants();
  };

  return (
    <main className="admit-applicants">

      <div className="admit-applicants__container">

        {/* =================================================
            PAGE HEADER
        ================================================= */}

        <header className="admission-page-header">

          <div className="admission-heading">

            <div className="admission-eyebrow">
              <span />
              ADMISSIONS MANAGEMENT
            </div>

            <h1>
              Recommended Applicants
            </h1>

            <p>
              Review, monitor and manage candidates
              recommended for admission.
            </p>

          </div>

          <button
            type="button"
            className="admission-refresh"
            onClick={getApplicants}
            disabled={loading}
          >
            <RefreshOutlined
              className={
                loading
                  ? "refresh-spinning"
                  : ""
              }
            />

            Refresh records
          </button>

        </header>

        {/* =================================================
            KPI CARDS
        ================================================= */}

        <section className="admission-stats">

          <StatCard
            icon={<GroupsOutlined />}
            value={applicants.length}
            label="All recommended"
            description="Candidates currently recommended"
            variant="primary"
          />

          <StatCard
            icon={<TrendingUpOutlined />}
            value={counts.nursings}
            label="Nursing"
            description="Candidates recommended for Nursing"
            variant="blue"
          />

          <StatCard
            icon={<CheckCircleOutlined />}
            value={counts.midwiferys}
            label="Midwifery"
            description="Candidates recommended for Midwifery"
            variant="yellow"
          />

          <StatCard
            icon={<AssignmentOutlined />}
            value={visibleApplicants.length}
            label="Visible records"
            description="Records matching your workspace"
            variant="neutral"
          />

        </section>

        {/* =================================================
            TOOLBAR
        ================================================= */}

        <section className="admission-toolbar">

          <div className="admission-search">

            <SearchOutlined />

            <input
              type="search"
              value={searchTerm}
              placeholder="Search application number, candidate, programme..."
              onChange={(event) =>
                setSearchTerm(
                  event.target.value
                )
              }
              onKeyDown={(event) => {
                if (event.key === "Enter") {
                  findApplicationNo();
                }
              }}
            />

            {searchTerm && (
              <button
                type="button"
                onClick={() => setSearchTerm("")}
              >
                <CloseOutlined />
              </button>
            )}

          </div>

          <button
            type="button"
            className="search-submit"
            onClick={findApplicationNo}
            disabled={loading}
          >
            <SearchOutlined />
            Search
          </button>

          <div className="admission-view-switcher">

            <button
              type="button"
              className={
                view === "table"
                  ? "active"
                  : ""
              }
              onClick={() => setView("table")}
              title="Table view"
            >
              <TableRowsOutlined />
            </button>

            <button
              type="button"
              className={
                view === "grid"
                  ? "active"
                  : ""
              }
              onClick={() => setView("grid")}
              title="Grid view"
            >
              <AppsOutlined />
            </button>

          </div>

        </section>

        {/* =================================================
            CONTENT HEADER
        ================================================= */}

        <div className="admission-content-heading">

          <div>
            <span>
              RECOMMENDED CANDIDATES
            </span>

            <strong>
              {visibleApplicants.length}{" "}
              {visibleApplicants.length === 1
                ? "candidate"
                : "candidates"}
            </strong>
          </div>

          {searchTerm && (
            <div className="search-context">
              <SearchOutlined />
              Searching:
              <strong>
                "{searchTerm}"
              </strong>
            </div>
          )}

        </div>

        {/* =================================================
            CONTENT
        ================================================= */}

        {visibleApplicants.length === 0 ? (
          <EmptyState
            searching={Boolean(searchTerm)}
            clearSearch={clearSearch}
          />
        ) : view === "table" ? (
          <TableView
            applicants={visibleApplicants}
          />
        ) : (
          <CardView
            applicants={visibleApplicants}
          />
        )}

        {/* =================================================
            LOADING INDICATOR
        ================================================= */}

        {loading && (
          <div className="admission-loading">

            <div className="admission-loading__spinner" />

            <span>
              Updating admission records...
            </span>

          </div>
        )}

      </div>

    </main>
  );
}